import React, { useEffect, useState } from 'react';
import { useAccessUuidRefresher } from 'containers/Otp/hooks/useAccessUuidRefresher';
import { useHistory, useRouteMatch } from 'react-router';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { useOtpQueries } from './useOtpQueries';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { VoucherHeader } from './VoucherHeader';
import { useVoucherQueries } from './useVoucherQueries';
import { getAccessUuidForItem, setAccessUuidForItem } from './utils';
import { SimpleTabs } from 'ui/SimpleTabs';
import _ from 'lodash';
import { HeaderCollapsible } from 'ui/HeaderCollapsible';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import {
  VoucherHotelsRenderer,
  VoucherGuestsRenderer,
  VoucherTransfersRenderer,
  VoucherGroundServicesRenderer,
  VoucherExperiencesRenderer,
  VoucherFurtherInformationRenderer,
} from './VoucherPageComponents';
import { VoucherAnnouncementsRenderer } from './components/VoucherAnnouncementsRenderer';
import { ImageUpload } from 'services/ItineraryManagerApi/types';
import { shouldRenderHideableText } from './components/common';
import { useTabFocus } from 'hooks/useTabFocus';

interface IVoucherPageRouteParams {
  voucherUuid: string;
}

export const VoucherPage = () => {
  const history = useHistory();
  const { dynamicParameters } = useDynamicParameters();
  const match = useRouteMatch<IVoucherPageRouteParams>();
  const { voucherUuid } = match.params;

  // State for tracking active tab on mobile
  const [activeTab, setActiveTab] = useState<string>('voucher-details');

  const isOtpEnabled = dynamicParameters.ENABLE_OTP_TEMP !== false;
  const storedAccessUuid = getAccessUuidForItem(voucherUuid);

  // Always check OTP status when enabled
  const otpQueries = useOtpQueries(makeItineraryManagerApi(), voucherUuid, storedAccessUuid, isOtpEnabled);

  // Extract the response data
  const otpCheckData = otpQueries.checkIsOtpRequired.data?.data;
  const responseAccessUuid = otpCheckData?.accessUuid;
  const otpRequired = otpCheckData?.otpRequired;

  // Update stored access UUID if we got a new one from the response
  useEffect(() => {
    if (isOtpEnabled && responseAccessUuid && responseAccessUuid !== storedAccessUuid) {
      setAccessUuidForItem(voucherUuid, responseAccessUuid);
    }
  }, [isOtpEnabled, responseAccessUuid, storedAccessUuid, voucherUuid]);

  // Determine which access UUID to use: new one from response or stored one
  const currentAccessUuid = responseAccessUuid || storedAccessUuid;

  // Refresh the accessUuid periodically
  useAccessUuidRefresher({
    accessUuid: currentAccessUuid,
    itemUuid: voucherUuid,
  });

  // if the user has been away from the page for more than 3 mins, refresh the page
  // this should take care of OWA-9489 by ensuring that if you've been away for a while,
  // the page will refresh (and thus you'll get a new token)
  useTabFocus(() => {
    window.location.reload();
  }, 180000);

  // Only fetch voucher data if we have an access UUID and OTP check is complete
  const shouldFetchVoucher =
    !isOtpEnabled || (otpQueries.checkIsOtpRequired.isSuccess && !otpRequired && currentAccessUuid);
  const voucherQueries = useVoucherQueries(
    makeItineraryManagerApi(),
    shouldFetchVoucher ? voucherUuid : undefined,
    currentAccessUuid
  );

  // Handle loading state
  if (isOtpEnabled && otpQueries.checkIsOtpRequired.isLoading) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar label="Loading voucher data..." />
      </div>
    );
  }

  if (voucherQueries.getVouchersV2View.isLoading) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <LoadingBar label="Loading voucher data..." />
      </div>
    );
  }

  if (voucherQueries.getVouchersV2View.isError) {
    return (
      <div className="container w-full min-w-full mx-auto mt-[100px]">
        <ErrorBar message="Error loading voucher data. Please refresh the page and try again." />
      </div>
    );
  }

  // Redirect to OTP page if required
  if (isOtpEnabled && otpRequired) {
    history.push(`/otp/${voucherUuid}?redirect=${window.location.pathname}`);
    return null;
  }

  const voucherData = voucherQueries.getVouchersV2View.data?.data;
  const guests = voucherData?.voucher?.guests || [];
  const guestsMoreInfo = voucherData?.voucher?.guestsMoreInfo;
  const hotels = voucherData?.voucher?.hotels || [];
  const transfers = voucherData?.voucher?.transfers || [];
  const groundServices = voucherData?.voucher?.groundServices || [];
  const experiences = voucherData?.voucher?.experiences || [];
  const specialRequests = voucherData?.voucher?.specialRequest;
  const specialRequestMoreInfo = voucherData?.voucher?.specialRequestMoreInfo;
  const allocations = voucherData?.allocations || [];

  const destinationFiles = voucherQueries.getVouchersV2View.data?.data?.destinationFiles || [];
  const destinationDetailsIsVisible = voucherData?.voucher?.destinationDetailsIsVisible ?? true;

  const peBookingNumber = voucherData?.voucher?.peBookingNumber;
  const travelPartnerReference = voucherData?.voucher?.travelPartnerReference;

  // Navigate to edit page
  const handleEdit = () => {
    history.push(`/voucher/${voucherUuid}/edit`);
  };

  const renderVoucherDetailsTab = () => {
    return (
      <div className="flex flex-col gap-20px w-full">
        <div className="flex gap-2">
          {shouldRenderHideableText(peBookingNumber) && (
            <span className="text-gray-100 bg-white-hard p-1 font-pt-sans text-[13px]">
              Booking Ref. {peBookingNumber!.text}
            </span>
          )}
          {shouldRenderHideableText(travelPartnerReference) && (
            <span className="text-gray-100 bg-white-hard p-1 font-pt-sans text-[13px]">
              TP Ref. {travelPartnerReference!.text}
            </span>
          )}
        </div>

        <VoucherGuestsRenderer guests={guests} guestsMoreInfo={guestsMoreInfo} />

        {/* if we have special requests or special request more info, and they're visible, show them here in an expandable thing */}
        {((specialRequests && specialRequests.isVisible) ||
          (specialRequestMoreInfo && specialRequestMoreInfo.isVisible)) && (
          <HeaderCollapsible
            titleClassname="o:font-normal o:font-pt-sans"
            title="Special Request"
            childrenClassname="font-pt-sans text-black text-[15px]"
          >
            <div className="whitespace-pre-wrap">
              {shouldRenderHideableText(specialRequests) && <div>{specialRequests!.text}</div>}
              {shouldRenderHideableText(specialRequests) && shouldRenderHideableText(specialRequestMoreInfo) && (
                <div className="mt-2"></div>
              )}
              {shouldRenderHideableText(specialRequestMoreInfo) && (
                <div>
                  <HtmlStringRenderer className="m-0" content={specialRequestMoreInfo!.text} />
                </div>
              )}
            </div>
          </HeaderCollapsible>
        )}

        {/* Hotel Information */}
        <VoucherHotelsRenderer hotels={hotels} allocations={allocations} guests={guests} />

        {/* Transfer Information */}
        <VoucherTransfersRenderer transfers={transfers} allocations={allocations} guests={guests} />

        {/* Ground Services Information */}
        <VoucherGroundServicesRenderer groundServices={groundServices} allocations={allocations} guests={guests} />

        {/* Experiences Information */}
        <VoucherExperiencesRenderer experiences={experiences} allocations={allocations} guests={guests} />

        {/* Further Information */}
        <VoucherFurtherInformationRenderer
          extraServices={voucherData?.voucher?.extraServices}
          additionalInformation={voucherData?.voucher?.additionalInformation}
          membershipBenefits={voucherData?.voucher?.membershipBenefits}
          policiesAndRestrictions={voucherData?.voucher?.policiesAndRestrictions}
          thingsToBeAwareOf={voucherData?.voucher?.thingsToBeAwareOf}
          emergencyContact={voucherData?.voucher?.emergencyContact}
          hotels={hotels}
        />
      </div>
    );
  };

  const uniqCountries = _.uniq(voucherData?.voucher.countries.map(country => country.name));

  // Create a mapping of country codes to names
  const countryCodeToName =
    voucherData?.voucher.countries.reduce((acc, country) => {
      acc[country.countryCode] = country.name;
      return acc;
    }, {} as Record<string, string>) || {};

  const tabsForDestinationFiles = destinationDetailsIsVisible
    ? destinationFiles.map((file, fileIdx) => {
        const countryName = countryCodeToName[file.countryCode] || `Destination ${fileIdx + 1}`;
        const tabTitle = `${countryName} Details`;

        return {
          title: tabTitle,
          name: tabTitle,
          styles: 'min-w-250px',
          renderContent: () => (
            // the 141 is a lil hack to make it so that its always rendering the correct height to display a single a4 page
            <div className="w-full relative" style={{ paddingBottom: '141.4%' }}>
              <iframe
                src={`${dynamicParameters.ITINERARY_MANAGER_BASE_URL}/viewer/v1/media/${file.path}#navpanes=0&toolbar=0&view=FitH`}
                width="100%"
                height="100%"
                title={tabTitle}
                className="border-0 absolute top-0 left-0 bg-white"
              />
            </div>
          ),
        };
      })
    : [];

  const voucherImage = voucherQueries.getVouchersV2View.data?.data?.voucher.voucherImage;

  // Generate available tabs for mobile navigation
  const availableTabs = [
    { id: 'voucher-details', label: 'Voucher Details' },
    ...(destinationDetailsIsVisible
      ? destinationFiles.map((file, fileIdx) => {
          const countryName = countryCodeToName[file.countryCode] || `Destination ${fileIdx + 1}`;
          return {
            id: `destination-${fileIdx + 1}`,
            label: `${countryName} Details`,
          };
        })
      : []),
  ];

  // Function to render PDF for mobile
  const renderPdfForMobile = (fileIndex: number) => {
    const file = destinationFiles[fileIndex];
    if (!file) return null;

    return (
      <div className="w-full relative" style={{ paddingBottom: '141.4%' }}>
        <iframe
          src={`${dynamicParameters.ITINERARY_MANAGER_BASE_URL}/viewer/v1/media/${file.path}#navpanes=0&toolbar=0&view=FitH`}
          width="100%"
          height="100%"
          title={`${countryCodeToName[file.countryCode] || `Destination ${fileIndex + 1}`} Details`}
          className="border-0 absolute top-0 left-0"
        />
      </div>
    );
  };

  // If OTP is not required, render the voucher page content.
  return (
    <div className="mb-40px">
      <VoucherHeader
        upload={voucherImage as ImageUpload}
        mode={'view'}
        onEdit={handleEdit}
        availableTabs={availableTabs}
        currentTab={activeTab}
        onTabChange={setActiveTab}
        voucherUuid={voucherUuid}
        accessUuid={currentAccessUuid}
        hotels={hotels}
        countries={voucherData?.voucher?.countries}
        destinationFiles={destinationFiles}
      />

      <div className="mx-[20px] md:mx-[80px]">
        <div className="mb-6">
          <h2 className="text-[72px] font-normal font-noe-display text-center my-0 mt-[20px] text-black">Voucher</h2>
          {uniqCountries.length > 0 && (
            <span className="flex gap-2 w-full justify-center my-0">
              <span className="font-noe-display font-normal text-[36px] text-black">{uniqCountries.join(' - ')}</span>
            </span>
          )}
        </div>

        {/* Desktop view - conditional tabs or direct content */}
        <div className="hidden md:block">
          {destinationFiles.length > 0 && destinationDetailsIsVisible ? (
            <SimpleTabs
              showBorders={false}
              forceEqualTabWidths={true}
              className="o:w-full"
              tabConfig={[
                {
                  title: 'Voucher Details',
                  name: 'voucher-details',
                  styles: 'min-w-150px',
                  renderContent: renderVoucherDetailsTab,
                },
                ...tabsForDestinationFiles,
              ]}
            />
          ) : (
            renderVoucherDetailsTab()
          )}
        </div>

        {/* Mobile view - conditional tabs or direct content */}
        <div className="block md:hidden">
          {destinationFiles.length > 0 && destinationDetailsIsVisible ? (
            <>
              <h3 className="font-pt-sans my-0 mb-20px w-full text-brown-prime pb-5px border-b border-brown-prime uppercase text-17px font-normal">
                {availableTabs.find(tab => tab.id === activeTab)?.label || 'Voucher Details'}
              </h3>

              {activeTab === 'voucher-details' && renderVoucherDetailsTab()}

              {activeTab.startsWith('destination-') &&
                (() => {
                  const destinationIndex = parseInt(activeTab.split('-')[1]) - 1;
                  return renderPdfForMobile(destinationIndex);
                })()}
            </>
          ) : (
            renderVoucherDetailsTab()
          )}
        </div>
      </div>
    </div>
  );
};
